import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getApiBaseUrl } from '@/lib/api';

interface Film {
  title: string;
  year: string;
}

interface Watchlist {
  letterboxdUrl: string | null;
  mode: 'manual' | 'sync';
  filmsCount: number;
  filmsUpdatedAt: string | null;
  createdAt: string;
  lastSyncAt: string | null;
  lastSyncMethod: 'server' | 'csv' | 'bookmarklet' | null;
  serverSyncEnabled: boolean;
  serverSyncBlocked: boolean;
}

export default function SyncPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [films, setFilms] = useState<Film[]>([]);
  const [watchlist, setWatchlist] = useState<Watchlist | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    count: number;
    filmsProcessed?: number;
    filmsAdded?: number;
    skippedDuplicates?: number;
    filmsDeleted?: number;
    mode: 'manual' | 'sync';
  } | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [showSyncWarning, setShowSyncWarning] = useState(false);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const token = await user.getIdToken();
        setIdToken(token);
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Parse film data from URL or postMessage
  useEffect(() => {
    // Get URL from params
    const url = searchParams.get('url');
    if (url) {
      setSourceUrl(decodeURIComponent(url));
    }

    // Get films from URL params
    const filmsParam = searchParams.get('films');
    if (filmsParam) {
      try {
        const parsedFilms = JSON.parse(decodeURIComponent(filmsParam));
        if (Array.isArray(parsedFilms)) {
          setFilms(parsedFilms);
        }
      } catch (e) {
        console.error('Failed to parse films from URL:', e);
      }
    }

    // Listen for postMessage (for large film lists)
    const handleMessage = (event: MessageEvent) => {
      if (
        event.origin === 'https://letterboxd.com' ||
        event.origin === 'https://www.letterboxd.com'
      ) {
        if (event.data?.type === 'SLF_SYNC_FILMS' && event.data.films) {
          setFilms(event.data.films);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [searchParams]);

  // Fetch watchlist when authenticated
  const fetchWatchlist = useCallback(async () => {
    if (!idToken) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/watchlist`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch watchlist');
      }

      const data = await response.json();
      setWatchlist(data.watchlist);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load watchlist');
    }
  }, [idToken]);

  useEffect(() => {
    if (idToken) {
      fetchWatchlist();
    }
  }, [idToken, fetchWatchlist]);

  const handleSyncClick = () => {
    if (!watchlist || films.length === 0) return;

    // In sync mode with existing films, show warning first
    if (watchlist.mode === 'sync' && watchlist.filmsCount > 0) {
      setShowSyncWarning(true);
      return;
    }

    // Otherwise proceed directly
    performSync();
  };

  const performSync = async () => {
    if (!idToken || !watchlist || films.length === 0) return;

    setIsSyncing(true);
    setError(null);
    setShowSyncWarning(false);

    try {
      const response = await fetch(`${getApiBaseUrl()}/watchlist/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ films }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync films');
      }

      setSuccess({
        count: data.filmsImported,
        filmsProcessed: data.filmsProcessed,
        filmsAdded: data.filmsAdded,
        skippedDuplicates: data.skippedDuplicates,
        filmsDeleted: data.filmsDeleted,
        mode: watchlist.mode,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync films');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLogin = () => {
    // Store return URL
    sessionStorage.setItem('syncReturnUrl', window.location.href);
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-primary mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-dark-500 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-display font-bold text-primary mb-2">
            Still Love Film
          </h1>
          <h2 className="text-lg font-medium text-dark">Sync Watchlist</h2>
        </div>

        {/* Sync Warning Modal (for sync mode) */}
        {showSyncWarning && watchlist && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-dark mb-2">
              Replace Existing Films?
            </h3>
            <p className="text-dark-500 mb-4">
              This will <span className="font-bold text-red-600">replace</span>{' '}
              all <span className="font-bold">{watchlist.filmsCount}</span>{' '}
              films currently in your watchlist with the{' '}
              <span className="font-bold">{films.length}</span> films from
              Letterboxd.
            </p>
            <p className="text-sm text-dark-400 mb-6">
              To add films without replacing, switch to Manual mode in Settings.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowSyncWarning(false)}
                className="px-6 py-3 rounded-lg font-medium text-dark-500 hover:bg-dark-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={performSync}
                disabled={isSyncing}
                className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {isSyncing ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Replacing...
                  </>
                ) : (
                  'Replace All Films'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Success State */}
        {success && !showSyncWarning && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-dark mb-2">
              Sync Successful!
            </h3>

            {/* Detailed breakdown */}
            <div className="text-left bg-dark-50 rounded-lg p-4 mb-4 space-y-2">
              <div className="flex justify-between text-dark-600">
                <span>Movies processed:</span>
                <span className="font-medium">
                  {success.filmsProcessed ?? success.count}
                </span>
              </div>

              {success.mode === 'manual' ? (
                <>
                  <div className="flex justify-between text-dark-500">
                    <span>Already in your list:</span>
                    <span className="font-medium">
                      {success.skippedDuplicates ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Added:</span>
                    <span className="font-bold">{success.filmsAdded ?? 0}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-green-600">
                    <span>Added:</span>
                    <span className="font-bold">{success.filmsAdded ?? 0}</span>
                  </div>
                  {success.filmsDeleted !== undefined &&
                    success.filmsDeleted > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Replaced:</span>
                        <span className="font-medium">
                          {success.filmsDeleted}
                        </span>
                      </div>
                    )}
                </>
              )}
            </div>

            <button
              onClick={() => window.close()}
              className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {/* Not Authenticated */}
        {!success && !isAuthenticated && (
          <div className="text-center py-4">
            <p className="text-dark-500 mb-4">
              {films.length > 0 ? (
                <>
                  Found <span className="font-bold">{films.length}</span> films
                  from Letterboxd.
                  <br />
                  Please sign in to sync them to your watchlist.
                </>
              ) : (
                'Please sign in to sync your Letterboxd watchlist.'
              )}
            </p>
            <button
              onClick={handleLogin}
              className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Sign In
            </button>
          </div>
        )}

        {/* Authenticated */}
        {!success && !showSyncWarning && isAuthenticated && (
          <>
            {/* Film count */}
            <div className="bg-dark-50 rounded-lg p-4 mb-4">
              <p className="text-dark-600">
                <span className="font-bold text-primary">{films.length}</span>{' '}
                films found from Letterboxd
              </p>
              {sourceUrl && (
                <p className="text-sm text-dark-400 truncate mt-1">
                  {sourceUrl}
                </p>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* No films */}
            {films.length === 0 && (
              <div className="text-center py-4 text-dark-400">
                <p>
                  No films received. Please try the bookmarklet again from your
                  Letterboxd watchlist page.
                </p>
              </div>
            )}

            {/* Watchlist info and sync button */}
            {films.length > 0 && watchlist && (
              <>
                {/* Current watchlist info */}
                <div className="mb-4 p-3 bg-primary-50 rounded-lg border border-primary-100">
                  <p className="text-sm text-dark-600">
                    <span className="font-medium">Mode:</span>{' '}
                    {watchlist.mode === 'manual' ? 'Manual' : 'Sync'}
                    {watchlist.filmsCount > 0 && (
                      <span className="text-dark-400">
                        {' '}
                        • {watchlist.filmsCount} film
                        {watchlist.filmsCount !== 1 ? 's' : ''} currently
                      </span>
                    )}
                  </p>
                  {watchlist.mode === 'manual' && (
                    <p className="text-xs text-dark-400 mt-1">
                      Films will be added to your existing watchlist
                    </p>
                  )}
                  {watchlist.mode === 'sync' && watchlist.filmsCount > 0 && (
                    <p className="text-xs text-yellow-600 mt-1">
                      This will replace your existing {watchlist.filmsCount}{' '}
                      film{watchlist.filmsCount !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleSyncClick}
                  disabled={isSyncing}
                  className="w-full bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isSyncing ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      {watchlist.mode === 'manual' ? 'Add' : 'Sync'}{' '}
                      {films.length} Films
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </>
                  )}
                </button>
              </>
            )}

            {/* No watchlist */}
            {films.length > 0 && !watchlist && (
              <div className="text-center py-4">
                <p className="text-dark-500 mb-4">
                  You don't have a watchlist set up yet. Please create one first
                  in your settings.
                </p>
                <button
                  onClick={() => navigate('/settings')}
                  className="text-primary hover:underline"
                >
                  Go to Settings
                </button>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-dark-100 text-center">
          <button
            onClick={() => window.close()}
            className="text-dark-400 hover:text-dark-600 text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
